// AdminPanel.js - Admin panel for managing curriculum files
// Read-only viewer for curriculum slides

import React, { useState, useEffect } from 'react';
import { API_BASE_URL, apiFetch } from '../config';
import { getCourseContent, saveCourseContent } from '../services/CourseContentService';
import { renderMessageContent } from '../utils';
import { getCurriculumUrl, getSlidesProacticesUrl } from '../App';
import MessageContent from './MessageContent';
import QuestionBankAdmin from './QuestionBankAdmin';
import ClassQuizAdmin from './ClassQuizAdmin';
import StudentAdmin from './StudentAdmin';

// Navigation menu items - add more sections here
const ADMIN_SECTIONS = [
  { id: 'home', label: 'Home', icon: '🏠' },
  { id: 'content-generator', label: 'Content Generator', icon: '🤖' },
  { id: 'curriculum', label: 'Curriculum Files', icon: '📁' },
  { id: 'practices', label: 'Practice Files', icon: '📝' },
  { id: 'slide-processor', label: 'Generate PySage Slide', icon: '🎓' },
  { id: 'practice-processor', label: 'Generate PySage Slide Practice', icon: '📝' },
  { id: 'question-bank', label: 'Question Bank', icon: '📦' },
  { id: 'class-quiz', label: 'Class Quiz', icon: '📋' },
  { id: 'students', label: 'Students', icon: '👥' },
  // Add more sections here:
  // { id: 'users', label: 'User Management', icon: '👥' },
  // { id: 'analytics', label: 'Analytics', icon: '📊' },
  // { id: 'settings', label: 'Settings', icon: '⚙️' },
];

const AdminPanel = ({ onClose, selectedSection }) => {
  const [activeSection, setActiveSection] = useState('home');

  const [slides, setSlides] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [slideContent, setSlideContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Practice files state
  const [practiceFiles, setPracticeFiles] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [practiceContent, setPracticeContent] = useState('');
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceError, setPracticeError] = useState(null);

  // Content Generator state
  const [courseStructure, setCourseStructure] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [contentStatus, setContentStatus] = useState({}); // { "slide01|topic": "exists" | "missing" | "generating" | "error" }
  const [generatingTopic, setGeneratingTopic] = useState(null); // Currently generating topic
  const [contentGenLoading, setContentGenLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null); // { chapter, topic, content } - last generated content
  const [generationProgress, setGenerationProgress] = useState(0); // 0-100 progress
  const [promptPreview, setPromptPreview] = useState(null); // { chapter, topic, systemPrompt, userPrompt } - for editing before send
  const [editableUserPrompt, setEditableUserPrompt] = useState(''); // Editable user prompt
  const [previewContent, setPreviewContent] = useState(null); // { chapterId, chapterTitle, topic, content } - for viewing existing content
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEditing, setPreviewEditing] = useState(false); // Edit mode toggle
  const [previewEditText, setPreviewEditText] = useState(''); // Editable text
  const [previewSaving, setPreviewSaving] = useState(false);

  // Slide Processor state
  const [slideFile, setSlideFile] = useState(null);
  const [slideProcessing, setSlideProcessing] = useState(false);
  const [slideResult, setSlideResult] = useState(null); // blob URL for download
  const [slideResultFilename, setSlideResultFilename] = useState('');
  const [slideError, setSlideError] = useState(null);
  const [slideDragOver, setSlideDragOver] = useState(false);

  // Practice Processor state
  const [practiceProcFile, setPracticeProcFile] = useState(null);
  const [practiceProcProcessing, setPracticeProcProcessing] = useState(false);
  const [practiceProcResult, setPracticeProcResult] = useState(null);
  const [practiceProcResultFilename, setPracticeProcResultFilename] = useState('');
  const [practiceProcError, setPracticeProcError] = useState(null);
  const [practiceProcDragOver, setPracticeProcDragOver] = useState(false);

  // Prevent body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Load slide list when curriculum section is opened, or reload when master section changes
  useEffect(() => {
    // Clear stale data when section changes
    setSlides([]); setPracticeFiles([]); setCourseStructure([]);
    setSelectedSlide(null); setSlideContent('');
    setSelectedPractice(null); setPracticeContent('');
    setContentStatus({}); setGeneratedContent(null); setPreviewContent(null);
  }, [selectedSection]);

  useEffect(() => {
    if (activeSection === 'curriculum' && slides.length === 0) {
      loadSlideList();
    }
    if (activeSection === 'practices' && practiceFiles.length === 0) {
      loadPracticeFiles();
    }
    if (activeSection === 'content-generator' && courseStructure.length === 0) {
      loadCourseStructure();
    }
  }, [activeSection, slides.length, practiceFiles.length, courseStructure.length]);

  const loadSlideList = async () => {
    setLoading(true);
    try {
      const response = await fetch(getCurriculumUrl('syllabus_mapping.json'));
      if (!response.ok) throw new Error('Failed to load syllabus mapping');
      const mapping = await response.json();
      
      // Convert to array of { date, filename, slideNumber }
      const slideList = Object.entries(mapping).map(([date, filename]) => ({
        date,
        filename,
        slideNumber: filename.match(/\d+/)?.[0] || '?',
        title: `Slide ${filename.match(/\d+/)?.[0] || '?'}`
      }));
      
      // Sort by slide number
      slideList.sort((a, b) => parseInt(a.slideNumber) - parseInt(b.slideNumber));
      
      setSlides(slideList);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Load slide content when selected
  const loadSlideContent = async (slide) => {
    setSelectedSlide(slide);
    setSlideContent('Loading...');
    
    try {
      const response = await fetch(getCurriculumUrl(slide.filename));
      if (!response.ok) throw new Error('Failed to load slide content');
      const content = await response.text();
      setSlideContent(content);
    } catch (err) {
      setSlideContent(`Error loading slide: ${err.message}`);
    }
  };

  // Load practice files list
  const loadPracticeFiles = async () => {
    setPracticeLoading(true);
    try {
      // Practice files follow pattern slideXX_practices.txt
      const practiceList = [
        { filename: 'slide01_practices.txt', slideNumber: '01', title: 'Ch 1 - Intro to Computers' },
        { filename: 'slide02_practices.txt', slideNumber: '02', title: 'Ch 2 - Elementary Programming' },
        { filename: 'slide03_practices.txt', slideNumber: '03', title: 'Ch 3 - Math, Strings, Objects' },
        { filename: 'slide04_practices.txt', slideNumber: '04', title: 'Ch 4 - Selections' },
        { filename: 'slide05_practices.txt', slideNumber: '05', title: 'Ch 5 - Loops' },
        { filename: 'slide06_practices.txt', slideNumber: '06', title: 'Ch 6 - Functions' },
        { filename: 'slide07_practices.txt', slideNumber: '07', title: 'Ch 7 - OOP' },
        { filename: 'slide08_practices.txt', slideNumber: '08', title: 'Ch 8 - Strings & Methods' },
        { filename: 'slide10_practices.txt', slideNumber: '10', title: 'Ch 10 - Lists' },
        { filename: 'slide11_practices.txt', slideNumber: '11', title: 'Ch 11 - 2D Lists' },
        { filename: 'slide14_practices.txt', slideNumber: '14', title: 'Ch 14 - Tuples, Sets, Dicts' },
      ];
      setPracticeFiles(practiceList);
      setPracticeLoading(false);
    } catch (err) {
      setPracticeError(err.message);
      setPracticeLoading(false);
    }
  };

  // Load practice file content when selected
  const loadPracticeContent = async (practice) => {
    setSelectedPractice(practice);
    setPracticeContent('Loading...');
    
    try {
      const response = await fetch(getSlidesProacticesUrl(practice.filename));
      if (!response.ok) throw new Error('Failed to load practice file');
      const content = await response.text();
      setPracticeContent(content);
    } catch (err) {
      setPracticeContent(`Error loading practice file: ${err.message}`);
    }
  };

  // Load course structure for Content Generator
  const loadCourseStructure = async () => {
    setContentGenLoading(true);
    try {
      const response = await fetch(getCurriculumUrl('course_structure.json'));
      if (!response.ok) throw new Error('Failed to load course structure');
      const structure = await response.json();
      setCourseStructure(structure);
      
      // Check content status for all topics
      await checkAllContentStatus(structure);
      setContentGenLoading(false);
    } catch (err) {
      console.error('Error loading course structure:', err);
      setContentGenLoading(false);
    }
  };

  // Check if content exists in DB for all topics
  const checkAllContentStatus = async (structure) => {
    const statusMap = {};
    
    for (const chapter of structure) {
      for (const topic of chapter.topics) {
        const key = `${chapter.id}|${topic}`;
        statusMap[key] = 'checking';
      }
    }
    setContentStatus(statusMap);

    // Check each topic (in parallel batches to avoid overwhelming the API)
    for (const chapter of structure) {
      for (const topic of chapter.topics) {
        const key = `${chapter.id}|${topic}`;
        try {
          const content = await getCourseContent(chapter.id, topic);
          statusMap[key] = content && content.content ? 'exists' : 'missing';
        } catch (err) {
          statusMap[key] = 'missing';
        }
      }
    }
    setContentStatus({ ...statusMap });
  };

  // Prepare prompt for preview (called when clicking Generate)
  const preparePrompt = async (chapter, topic) => {
    setGeneratedContent(null);
    setPreviewContent(null);
    setGenerationProgress(10);
    
    try {
      // Find chapter index
      const chapterIndex = courseStructure.findIndex(c => c.id === chapter.id);
      
      // Load curriculum content for this slide and all previous slides (cumulative)
      let cumulativeContent = '';
      for (let i = 0; i <= chapterIndex; i++) {
        const slideFile = courseStructure[i].id + '.txt';
        try {
          const response = await fetch(getCurriculumUrl(slideFile));
          if (response.ok) {
            const content = await response.text();
            cumulativeContent += `\n\n--- Chapter ${i + 1}: ${courseStructure[i].title} ---\n${content}`;
          }
        } catch (e) {
          console.error('Error loading curriculum file:', slideFile);
        }
      }

      // Get future chapter titles for lockdown
      const futureChapterTitles = courseStructure
        .slice(chapterIndex + 1)
        .map((ch, i) => `- Chapter ${chapterIndex + 2 + i}: ${ch.title}`)
        .join('\n');

      // Check if specific concepts are in future chapters
      const futureChapterTitlesLower = courseStructure
        .slice(chapterIndex + 1)
        .map(ch => ch.title.toLowerCase());
      
      const listsInFuture = futureChapterTitlesLower.some(t => t.includes('list'));
      const functionsInFuture = futureChapterTitlesLower.some(t => t.includes('function'));
      const dictionariesInFuture = futureChapterTitlesLower.some(t => t.includes('dictionar'));
      const loopsInFuture = futureChapterTitlesLower.some(t => t.includes('loop'));
      const classesInFuture = futureChapterTitlesLower.some(t => t.includes('class') || t.includes('oop') || t.includes('object-oriented'));

      // Build dynamic concept restrictions
      const conceptRestrictions = [
        listsInFuture ? '- "Lists" in future? → NO [] syntax, NO append(), NO list operations, NO ["item1", "item2"]' : null,
        functionsInFuture ? '- "Functions" in future? → NO def, NO return, NO function definitions' : null,
        dictionariesInFuture ? '- "Dictionaries" in future? → NO {} with key:value pairs' : null,
        loopsInFuture ? '- "Loops" in future? → NO for, NO while loops' : null,
        classesInFuture ? '- "Classes" in future? → NO class definitions' : null,
      ].filter(Boolean).join('\n');

      const loopListWarning = listsInFuture ? `

🔴 LOOP EXAMPLES - CRITICAL (Lists chapter is in a FUTURE chapter):
When teaching loops, ALWAYS use range() or string iteration, NEVER lists:
   ❌ WRONG: fruits = ["apple", "banana", "cherry"]
             for fruit in fruits:
   ❌ WRONG: for item in [1, 2, 3]:
   ❌ WRONG: for x in ["a", "b", "c"]:
   
   ✅ CORRECT: for i in range(1, 4):
   ✅ CORRECT: for i in range(3):
   ✅ CORRECT: for char in "hello":
   ✅ CORRECT: for i in range(1, 6):  # iterate 1 to 5` : '';

      // Build curriculum lockdown section
      const curriculumLockdown = cumulativeContent 
        ? `\n\n[CURRICULUM LOCKDOWN - CRITICAL - READ CAREFULLY]
You are teaching Chapter ${chapterIndex + 1}: "${chapter.title}"

✅ LEARNED CHAPTERS (you CAN use these concepts):
${cumulativeContent}

🚫 FUTURE CHAPTERS - ABSOLUTELY FORBIDDEN:
${futureChapterTitles || 'None - student has completed all chapters'}

⛔ STRICT RULES - VIOLATION WILL CONFUSE THE STUDENT:
1. ONLY use concepts from LEARNED CHAPTERS above
2. Check each FUTURE CHAPTER TITLE - if a concept is taught there, DO NOT USE IT:
${conceptRestrictions || '   (No specific restrictions - student has learned most concepts)'}
${loopListWarning}

3. If the student asks about an ADVANCED topic from a later chapter, respond:
   "That's a great question! That topic is covered in a later chapter. For now, we're focusing on Chapter ${chapterIndex + 1}.
   Would you like a quick preview (no coding yet) or stay focused on the current chapter?"

[END CURRICULUM LOCKDOWN]`
        : '';

      // Create the teaching system prompt (same as CourseContentView)
      const systemPrompt = `You are an expert Python tutor helping a student learn "${topic}" from the chapter "${chapter.title}".
${curriculumLockdown}

YOUR TEACHING FLOW:

## STEP 1: TEACH (do this first)
- **Introduction** (6-8 sentences): What this topic is, why it matters, and real-world applications.
- **Core Concepts** (4-6 paragraphs): Explain clearly with simple language, analogies, and common pitfalls to avoid.
- **Code Examples**: Show 6-8 examples (10-20 lines each) with expected output and detailed explanations.

## STEP 2: OFFER PRACTICE (after teaching)
End your teaching with this exact question:
"🎯 **Ready to practice?** Would you like:
1. **Quiz** - Test your understanding with multiple choice questions
2. **Coding Exercise** - Try writing some simple code
3. **Both** - Do quiz first, then coding

Just reply with 1, 2, 3, or ask me any questions!"

CODE FORMATTING (CRITICAL - MUST FOLLOW):
When showing ANY Python code, you MUST use markdown code fences. Write three backtick characters, then "python", then newline, then your code, then newline, then three backtick characters.

When showing program OUTPUT, you MUST also use code fences. Write three backtick characters, then newline, then the output, then newline, then three backtick characters. Label it clearly as "Output:" before the code fence.

⚠️ PYTHON INDENTATION - CRITICAL:
- TOP-LEVEL code (variables, for, while, if, print outside blocks) starts at COLUMN 0 - NO SPACES
- Code INSIDE a block (after :) gets exactly 4 spaces
- When a block ends, return to previous indent level (usually 0)
- CONSECUTIVE statements at the same level must have the SAME indentation

Start teaching now with Step 1!`;

      const defaultUserPrompt = `Please teach me about "${topic}". Start from the basics.`;
      
      // Show preview
      setPromptPreview({
        chapter: chapter,
        topic: topic,
        systemPrompt: systemPrompt,
        userPrompt: defaultUserPrompt
      });
      setEditableUserPrompt(defaultUserPrompt);
      setGenerationProgress(0);
      
    } catch (err) {
      console.error('Error preparing prompt:', err);
      setGenerationProgress(0);
    }
  };

  // Actually send to LLM (called after preview confirmation)
  const sendToLLM = async () => {
    if (!promptPreview) return;
    
    const { chapter, topic, systemPrompt } = promptPreview;
    const key = `${chapter.id}|${topic}`;
    
    setGeneratingTopic(key);
    setContentStatus(prev => ({ ...prev, [key]: 'generating' }));
    setPromptPreview(null); // Close preview
    setGenerationProgress(50);

    try {
      // Call OpenAI API
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: editableUserPrompt }
          ],
          temperature: 0.4,
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get LLM response');
      }

      setGenerationProgress(80);

      const data = await response.json();
      const contentGenerated = data.choices[0].message.content;

      // Save to database
      const username = localStorage.getItem('username') || 'admin';
      const saved = await saveCourseContent(chapter.id, topic, contentGenerated, username);

      if (saved) {
        setGenerationProgress(100);
        setContentStatus(prev => ({ ...prev, [key]: 'exists' }));
        setGeneratedContent({
          chapter: chapter.title,
          topic: topic,
          content: contentGenerated
        });
      } else {
        throw new Error('Failed to save to database');
      }

    } catch (err) {
      console.error('Error generating content:', err);
      setContentStatus(prev => ({ ...prev, [key]: 'error' }));
      setGeneratedContent({
        chapter: chapter.title,
        topic: topic,
        content: null,
        error: err.message
      });
    } finally {
      setGeneratingTopic(null);
      setGenerationProgress(0);
    }
  };

  // Cancel prompt preview
  const cancelPromptPreview = () => {
    setPromptPreview(null);
    setEditableUserPrompt('');
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  // Load and preview existing content for a topic
  const handleViewContent = async (chapter, topic) => {
    setPreviewLoading(true);
    setPreviewContent(null);
    setGeneratedContent(null);
    setPromptPreview(null);
    try {
      const content = await getCourseContent(chapter.id, topic);
      if (content && content.content) {
        setPreviewContent({
          chapterId: chapter.id,
          chapterTitle: chapter.title,
          topic: topic,
          content: content.content,
        });
        setPreviewEditing(false);
        setPreviewEditText('');
      }
    } catch (err) {
      console.error('Failed to load content:', err);
    }
    setPreviewLoading(false);
  };

  // Render Content Generator section
  const renderContentGeneratorSection = () => {
    const totalTopics = courseStructure.reduce((sum, ch) => sum + ch.topics.length, 0);
    const existingCount = Object.values(contentStatus).filter(s => s === 'exists').length;
    const missingCount = Object.values(contentStatus).filter(s => s === 'missing').length;

    return (
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* Left Panel - Chapter List */}
        <div style={{
          width: '450px',
          backgroundColor: '#2d333b',
          borderRight: '1px solid rgba(255, 215, 102, 0.25)',
          overflowY: 'auto',
          padding: '16px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '8px' }}>
              🤖 Course Content Generator
            </h3>
            <p style={{ color: '#f5e6a3', fontSize: '13px', marginBottom: '12px' }}>
              Generate and cache LLM content for all topics
            </p>
            <div style={{ display: 'flex', gap: '12px', fontSize: '13px' }}>
              <span style={{ color: '#3fb950' }}>✅ {existingCount} cached</span>
              <span style={{ color: '#f0883e' }}>⏳ {missingCount} missing</span>
              <span style={{ color: '#f5e6a3' }}>📊 {totalTopics} total</span>
            </div>
          </div>

          {contentGenLoading ? (
            <p style={{ color: '#f5e6a3' }}>Loading course structure...</p>
          ) : (
            courseStructure.map((chapter, chapterIdx) => (
              <div key={chapter.id} style={{ marginBottom: '8px' }}>
                {/* Chapter Header */}
                <div
                  onClick={() => toggleChapter(chapter.id)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#373e47',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(255, 215, 102, 0.25)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#f5e6a3' }}>
                      {expandedChapters[chapter.id] ? '▼' : '▶'}
                    </span>
                    <span style={{ color: '#fff', fontWeight: '500' }}>
                      Ch {chapterIdx + 1}: {chapter.title}
                    </span>
                  </div>
                  <span style={{ color: '#f5e6a3', fontSize: '12px' }}>
                    {chapter.topics.length} topics
                  </span>
                </div>

                {/* Topics List */}
                {expandedChapters[chapter.id] && (
                  <div style={{ marginLeft: '20px', marginTop: '8px' }}>
                    {chapter.topics.map((topic, topicIdx) => {
                      const key = `${chapter.id}|${topic}`;
                      const status = contentStatus[key] || 'checking';
                      const isGenerating = generatingTopic === key;

                      return (
                        <div
                          key={topicIdx}
                          style={{
                            padding: '10px 14px',
                            marginBottom: '6px',
                            backgroundColor: '#2d333b',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 215, 102, 0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ flex: 1, marginRight: '12px' }}>
                            <span style={{ color: '#c9d1d9', fontSize: '13px' }}>{topic}</span>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Status Indicator */}
                            {status === 'checking' && (
                              <span style={{ color: '#f5e6a3', fontSize: '12px' }}>⏳</span>
                            )}
                            {status === 'exists' && (
                              <>
                                <span style={{ color: '#3fb950', fontSize: '14px' }} title="Content exists in DB">✅</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewContent(chapter, topic); }}
                                  disabled={generatingTopic !== null}
                                  style={{
                                    padding: '2px 8px',
                                    fontSize: '10px',
                                    backgroundColor: 'transparent',
                                    color: '#58a6ff',
                                    border: '1px solid #58a6ff',
                                    borderRadius: '4px',
                                    cursor: generatingTopic !== null ? 'not-allowed' : 'pointer',
                                    opacity: generatingTopic !== null ? 0.5 : 1
                                  }}
                                  title="View generated content"
                                >👁️ View</button>
                              </>
                            )}
                            {status === 'missing' && (
                              <span style={{ color: '#f0883e', fontSize: '14px' }} title="Not generated yet">⏳</span>
                            )}
                            {status === 'generating' && (
                              <span style={{ color: '#58a6ff', fontSize: '12px' }}>⚡</span>
                            )}
                            {status === 'error' && (
                              <span style={{ color: '#f85149', fontSize: '14px' }} title="Error generating">❌</span>
                            )}

                            {/* Generate Button */}
                            <button
                              onClick={() => preparePrompt(chapter, topic)}
                              disabled={isGenerating || generatingTopic !== null || promptPreview !== null}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                backgroundColor: isGenerating ? 'rgba(255, 215, 102, 0.25)' : (status === 'exists' ? '#21262d' : '#238636'),
                                color: isGenerating ? '#f5e6a3' : '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isGenerating || generatingTopic !== null || promptPreview !== null ? 'not-allowed' : 'pointer',
                                opacity: (generatingTopic !== null || promptPreview !== null) && !isGenerating ? 0.5 : 1
                              }}
                            >
                              {isGenerating ? '⚡ Generating...' : (status === 'exists' ? '🔄 Regen' : '⚡ Generate')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Right Panel - Progress & Generated Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: promptPreview && !generatingTopic ? 'hidden' : 'auto',
          backgroundColor: '#1c2128'
        }}>
          {/* Prompt Preview - shown before sending to LLM */}
          {promptPreview && !generatingTopic && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#388bfd20',
                borderRadius: '8px',
                border: '1px solid #388bfd'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>📝</span>
                  <div>
                    <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                      {promptPreview.chapter.title}
                    </div>
                    <div style={{ color: '#f5e6a3', fontSize: '13px' }}>
                      {promptPreview.topic}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={cancelPromptPreview}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#373e47',
                      color: '#c9d1d9',
                      border: '1px solid rgba(255, 215, 102, 0.25)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendToLLM}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#238636',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    🚀 Send to LLM
                  </button>
                </div>
              </div>

              {/* System Prompt (read-only) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#f5e6a3', fontSize: '12px', fontWeight: '500', marginBottom: '6px', display: 'block' }}>
                  SYSTEM PROMPT (read-only)
                </label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#2d333b',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 215, 102, 0.25)',
                  color: '#f0dca0',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace'
                }}>
                  {promptPreview.systemPrompt}
                </div>
              </div>

              {/* User Prompt (editable) */}
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '48px' }}>
                <label style={{ color: '#58a6ff', fontSize: '12px', fontWeight: '500', marginBottom: '6px', display: 'block' }}>
                  USER PROMPT (editable) ✏️
                </label>
                <textarea
                  value={editableUserPrompt}
                  onChange={(e) => setEditableUserPrompt(e.target.value)}
                  rows={10}
                  style={{
                    minHeight: '200px',
                    height: 'auto',
                    padding: '12px',
                    backgroundColor: '#1c2128',
                    borderRadius: '6px',
                    border: '2px solid #388bfd',
                    color: '#fff',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter your prompt here..."
                />
                <p style={{ color: '#f5e6a3', fontSize: '11px', marginTop: '8px' }}>
                  💡 Tip: You can edit the user prompt above to customize what content is generated. The system prompt contains curriculum rules and cannot be edited.
                </p>
              </div>
            </div>
          )}

          {/* Progress - shown while generating */}
          {generatingTopic && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '200px'
            }}>
              {/* Rotating spinner */}
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #21262d',
                borderTop: '4px solid #238636',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }} />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                Generating Content...
              </div>
              <div style={{ color: '#f5e6a3', fontSize: '13px' }}>
                {generationProgress < 30 && 'Loading curriculum...'}
                {generationProgress >= 30 && generationProgress < 50 && 'Building prompt...'}
                {generationProgress >= 50 && generationProgress < 80 && 'Calling LLM (this may take a few seconds)...'}
                {generationProgress >= 80 && generationProgress < 100 && 'Saving to database...'}
                {generationProgress >= 100 && 'Done!'}
              </div>
            </div>
          )}

          {/* Generated Content Display */}
          {generatedContent && !generatingTopic && !promptPreview && (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: generatedContent.error ? '#f8514920' : '#23863620',
                borderRadius: '8px',
                border: `1px solid ${generatedContent.error ? '#f85149' : '#238636'}`
              }}>
                <span style={{ fontSize: '20px' }}>{generatedContent.error ? '❌' : '✅'}</span>
                <div>
                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                    {generatedContent.chapter}
                  </div>
                  <div style={{ color: '#f5e6a3', fontSize: '13px' }}>
                    {generatedContent.topic}
                  </div>
                </div>
              </div>

              {generatedContent.error ? (
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#2d333b', 
                  borderRadius: '8px',
                  color: '#f85149'
                }}>
                  Error: {generatedContent.error}
                </div>
              ) : (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#373e47',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 215, 102, 0.25)',
                  color: '#f0dca0',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  maxHeight: 'calc(100vh - 280px)',
                  overflowY: 'auto'
                }}>
                  <MessageContent content={renderMessageContent(generatedContent.content)} showTryIt={false} />
                </div>
              )}
            </div>
          )}

          {/* Preview existing content */}
          {previewContent && !generatingTopic && !promptPreview && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: previewEditing ? '#ffd70015' : '#388bfd15',
                borderRadius: '8px',
                border: `1px solid ${previewEditing ? '#8b7500' : '#388bfd'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{previewEditing ? '✏️' : '👁️'}</span>
                  <div>
                    <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                      {previewContent.chapterTitle}
                    </div>
                    <div style={{ color: previewEditing ? '#ffd700' : '#58a6ff', fontSize: '13px' }}>
                      {previewContent.topic}
                      {previewEditing && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#ffd700' }}>— Editing</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!previewEditing && (
                    <button
                      onClick={() => { setPreviewEditing(true); setPreviewEditText(previewContent.content); }}
                      style={{
                        padding: '6px 12px', fontSize: '12px', borderRadius: '4px',
                        backgroundColor: '#373e47', color: '#ffd700',
                        border: '1px solid #8b7500', cursor: 'pointer'
                      }}
                    >✏️ Edit</button>
                  )}
                  {previewEditing && (
                    <>
                      <button
                        onClick={() => setPreviewEditing(false)}
                        style={{
                          padding: '6px 12px', fontSize: '12px', borderRadius: '4px',
                          backgroundColor: '#373e47', color: '#c9d1d9',
                          border: '1px solid #484f58', cursor: 'pointer'
                        }}
                      >Cancel</button>
                      <button
                        onClick={async () => {
                          setPreviewSaving(true);
                          try {
                            const username = localStorage.getItem('username') || 'admin';
                            const saved = await saveCourseContent(previewContent.chapterId, previewContent.topic, previewEditText, username);
                            if (saved) {
                              setPreviewContent(prev => ({ ...prev, content: previewEditText }));
                              setPreviewEditing(false);
                            } else {
                              alert('Failed to save. Please try again.');
                            }
                          } catch (err) {
                            console.error('Save error:', err);
                            alert('Error saving: ' + err.message);
                          }
                          setPreviewSaving(false);
                        }}
                        disabled={previewSaving}
                        style={{
                          padding: '6px 12px', fontSize: '12px', borderRadius: '4px',
                          backgroundColor: '#238636', color: '#fff',
                          border: 'none', cursor: previewSaving ? 'not-allowed' : 'pointer',
                          opacity: previewSaving ? 0.7 : 1
                        }}
                      >{previewSaving ? '⏳ Saving...' : '💾 Save'}</button>
                    </>
                  )}
                  <button
                    onClick={() => { setPreviewContent(null); setPreviewEditing(false); }}
                    style={{
                      background: 'none', border: 'none', color: '#f0f6fc',
                      fontSize: '20px', cursor: 'pointer', fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#f85149'}
                    onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
                  >✕</button>
                </div>
              </div>

              {/* Read-only view */}
              {!previewEditing && (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#373e47',
                  borderRadius: '8px',
                  border: '1px solid rgba(56, 139, 253, 0.3)',
                  color: '#f0dca0',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  flex: 1,
                  overflowY: 'auto'
                }}>
                  <MessageContent content={renderMessageContent(previewContent.content)} showTryIt={false} />
                </div>
              )}

              {/* Edit mode */}
              {previewEditing && (
                <textarea
                  value={previewEditText}
                  onChange={(e) => setPreviewEditText(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '16px',
                    backgroundColor: '#1c2128',
                    borderRadius: '8px',
                    border: '2px solid #8b7500',
                    color: '#e6edf3',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    fontFamily: 'monospace',
                    resize: 'none',
                    outline: 'none',
                  }}
                  placeholder="Edit content here..."
                />
              )}
            </div>
          )}

          {/* Loading preview */}
          {previewLoading && (
            <div style={{ color: '#58a6ff', textAlign: 'center', padding: '40px', fontSize: '14px' }}>
              ⏳ Loading content...
            </div>
          )}

          {/* Empty state */}
          {!generatingTopic && !generatedContent && !previewContent && !previewLoading && !promptPreview && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              color: '#f5e6a3',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '48px', marginBottom: '16px' }}>📝</span>
              <p style={{ maxWidth: '400px', lineHeight: '1.6' }}>
                Click "Generate" on any topic to create content,
                or click "👁️ View" to preview existing content.
                <br /><br />
                The generated content will be cached in the database and served to students instantly.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render the Home section
  const renderHomeSection = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: '#f5e6a3',
      padding: '40px'
    }}>
      <span style={{ fontSize: '64px', marginBottom: '24px' }}>⚙️</span>
      <h2 style={{ color: '#fff', marginBottom: '16px' }}>Welcome to Admin Panel</h2>
      <p style={{ textAlign: 'center', maxWidth: '500px', lineHeight: '1.6' }}>
        Select a section from the sidebar to manage your Python Teacher App.
      </p>
    </div>
  );

  // Render the Curriculum Files section
  const renderCurriculumSection = () => (
    <div style={{
      flex: 1,
      display: 'flex',
      overflow: 'hidden'
    }}>
      {/* Slide List */}
      <div style={{
        width: '280px',
        backgroundColor: '#2d333b',
        borderRight: '1px solid rgba(255, 215, 102, 0.25)',
        overflowY: 'auto',
        padding: '16px'
      }}>
        <h3 style={{ color: '#fff', fontSize: '14px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Slides ({slides.length})
        </h3>
        
        {loading && (
          <p style={{ color: '#f5e6a3' }}>Loading slides...</p>
        )}
        
        {error && (
          <p style={{ color: '#f85149' }}>Error: {error}</p>
        )}
        
        {!loading && !error && slides.map((slide) => (
          <div
            key={slide.filename}
            onClick={() => loadSlideContent(slide)}
            style={{
              padding: '12px 16px',
              marginBottom: '8px',
              backgroundColor: selectedSlide?.filename === slide.filename ? '#388bfd30' : '#21262d',
              borderRadius: '8px',
              cursor: 'pointer',
              border: selectedSlide?.filename === slide.filename ? '1px solid #58a6ff' : '1px solid rgba(255, 215, 102, 0.25)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (selectedSlide?.filename !== slide.filename) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 215, 102, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedSlide?.filename !== slide.filename) {
                e.currentTarget.style.backgroundColor = '#373e47';
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                backgroundColor: '#238636',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {slide.slideNumber}
              </span>
              <span style={{ color: '#c9d1d9', fontSize: '14px' }}>
                {slide.filename}
              </span>
            </div>
            <div style={{ 
              color: '#f5e6a3', 
              fontSize: '12px', 
              marginTop: '6px',
              marginLeft: '42px'
            }}>
              📅 {slide.date}
            </div>
          </div>
        ))}
      </div>

      {/* Slide Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
        backgroundColor: '#1c2128'
      }}>
        {!selectedSlide ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#f5e6a3'
          }}>
            <span style={{ fontSize: '48px', marginBottom: '16px' }}>📄</span>
            <p>Select a slide from the left to view its content</p>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#fff', margin: 0 }}>
                {selectedSlide.filename}
              </h2>
              <span style={{ 
                color: '#f5e6a3', 
                fontSize: '14px',
                backgroundColor: '#373e47',
                padding: '6px 12px',
                borderRadius: '6px'
              }}>
                Scheduled: {selectedSlide.date}
              </span>
            </div>
            
            <pre style={{
              backgroundColor: '#2d333b',
              border: '1px solid rgba(255, 215, 102, 0.25)',
              borderRadius: '8px',
              padding: '20px',
              color: '#c9d1d9',
              fontSize: '14px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              margin: 0,
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto'
            }}>
              {slideContent}
            </pre>
          </div>
        )}
      </div>
    </div>
  );

  // Render the Practice Files section
  const renderPracticesSection = () => (
    <div style={{
      flex: 1,
      display: 'flex',
      overflow: 'hidden'
    }}>
      {/* Practice Files List */}
      <div style={{
        width: '280px',
        backgroundColor: '#2d333b',
        borderRight: '1px solid rgba(255, 215, 102, 0.25)',
        overflowY: 'auto',
        padding: '16px'
      }}>
        <h3 style={{ color: '#fff', fontSize: '14px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Practice Files ({practiceFiles.length})
        </h3>
        
        {practiceLoading && (
          <p style={{ color: '#f5e6a3' }}>Loading practice files...</p>
        )}
        
        {practiceError && (
          <p style={{ color: '#f85149' }}>Error: {practiceError}</p>
        )}
        
        {!practiceLoading && !practiceError && practiceFiles.map((practice) => (
          <div
            key={practice.filename}
            onClick={() => loadPracticeContent(practice)}
            style={{
              padding: '12px 16px',
              marginBottom: '8px',
              backgroundColor: selectedPractice?.filename === practice.filename ? '#388bfd30' : '#21262d',
              borderRadius: '8px',
              cursor: 'pointer',
              border: selectedPractice?.filename === practice.filename ? '1px solid #58a6ff' : '1px solid rgba(255, 215, 102, 0.25)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (selectedPractice?.filename !== practice.filename) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 215, 102, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedPractice?.filename !== practice.filename) {
                e.currentTarget.style.backgroundColor = '#373e47';
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                backgroundColor: '#aff5b4',
                color: '#000',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {practice.slideNumber}
              </span>
              <span style={{ color: '#c9d1d9', fontSize: '13px' }}>
                {practice.title}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Practice Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
        backgroundColor: '#1c2128'
      }}>
        {!selectedPractice ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#f5e6a3'
          }}>
            <span style={{ fontSize: '48px', marginBottom: '16px' }}>📝</span>
            <p>Select a practice file from the left to view its content</p>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#fff', margin: 0 }}>
                {selectedPractice.title}
              </h2>
              <span style={{ 
                color: '#f5e6a3', 
                fontSize: '14px',
                backgroundColor: '#373e47',
                padding: '6px 12px',
                borderRadius: '6px'
              }}>
                {selectedPractice.filename}
              </span>
            </div>
            
            <pre style={{
              backgroundColor: '#2d333b',
              border: '1px solid rgba(255, 215, 102, 0.25)',
              borderRadius: '8px',
              padding: '20px',
              color: '#c9d1d9',
              fontSize: '14px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              margin: 0,
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto'
            }}>
              {practiceContent}
            </pre>
          </div>
        )}
      </div>
    </div>
  );

  // ==================== SLIDE PROCESSOR ====================
  const handleSlideUpload = async () => {
    if (!slideFile) return;
    setSlideProcessing(true);
    setSlideError(null);
    setSlideResult(null);
    try {
      const formData = new FormData();
      formData.append('file', slideFile);
      const response = await apiFetch(`${API_BASE_URL}/api/process-slide`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }
      // Get filename from Content-Disposition header or derive from uploaded file
      const disposition = response.headers.get('Content-Disposition');
      let filename;
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*?)\1(;|$)/);
        if (match) filename = match[2];
      }
      if (!filename) {
        const origName = slideFile.name;
        const dotIndex = origName.lastIndexOf('.');
        if (dotIndex > 0) {
          filename = origName.substring(0, dotIndex) + '_PySage_Processed' + origName.substring(dotIndex);
        } else {
          filename = origName + '_PySage_Processed';
        }
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setSlideResult(url);
      setSlideResultFilename(filename);
    } catch (err) {
      console.error('Slide processing error:', err);
      setSlideError(err.message);
    } finally {
      setSlideProcessing(false);
    }
  };

  const handleSlideDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSlideDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSlideFile(e.dataTransfer.files[0]);
      setSlideResult(null);
      setSlideError(null);
    }
  };

  const renderSlideProcessorSection = () => (
    <div style={{ padding: '32px', overflowY: 'auto', height: '100%' }}>
      <style>{`
        @keyframes slideSpinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <h2 style={{ color: '#aff5b4', marginBottom: '8px', fontSize: '24px' }}>🎓 Generate PySage Slide</h2>
      <p style={{ color: '#f5e6a3', marginBottom: '24px', fontSize: '14px' }}>
        Upload a slide file to process it via the PySage backend. The processed file will be available for download.
      </p>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setSlideDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setSlideDragOver(false); }}
        onDrop={handleSlideDrop}
        onClick={() => document.getElementById('slide-file-input').click()}
        style={{
          border: slideDragOver ? '2px solid #58a6ff' : '2px dashed #f5e6a3',
          borderRadius: '12px',
          padding: '48px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: slideDragOver ? '#388bfd15' : '#161b22',
          transition: 'all 0.2s',
          marginBottom: '24px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#58a6ff'; e.currentTarget.style.backgroundColor = '#388bfd10'; }}
        onMouseLeave={(e) => {
          if (!slideDragOver) {
            e.currentTarget.style.borderColor = '#f5e6a3';
            e.currentTarget.style.backgroundColor = '#2d333b';
          }
        }}
      >
        <input
          id="slide-file-input"
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              setSlideFile(e.target.files[0]);
              setSlideResult(null);
              setSlideError(null);
            }
          }}
        />
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📂</span>
        <p style={{ color: '#e6edf3', fontSize: '16px', margin: '0 0 4px 0' }}>
          Drag & drop a slide file here
        </p>
        <p style={{ color: '#f5e6a3', fontSize: '13px', margin: 0 }}>
          or click to browse
        </p>
      </div>

      {/* Selected File Info */}
      {slideFile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', backgroundColor: '#2d333b',
          borderRadius: '8px', marginBottom: '16px', border: '1px solid rgba(255, 215, 102, 0.25)'
        }}>
          <span style={{ fontSize: '20px' }}>📄</span>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px', fontWeight: '500' }}>{slideFile.name}</p>
            <p style={{ color: '#f5e6a3', margin: 0, fontSize: '12px' }}>{(slideFile.size / 1024).toFixed(1)} KB</p>
          </div>
          <span style={{ color: '#3fb950', fontSize: '16px' }}>✓ Selected</span>
          <button
            onClick={(e) => { e.stopPropagation(); setSlideFile(null); setSlideResult(null); setSlideError(null); }}
            style={{
              background: 'none', border: 'none', color: '#f85149',
              cursor: 'pointer', fontSize: '16px', padding: '4px'
            }}
            onMouseOver={(e) => e.target.style.color = '#ff0000'}
            onMouseOut={(e) => e.target.style.color = '#f85149'}
          >✕</button>
        </div>
      )}

      {/* Upload Button */}
      <button
        onClick={handleSlideUpload}
        disabled={!slideFile || slideProcessing}
        style={{
          padding: '12px 24px',
          backgroundColor: 'transparent',
          color: !slideFile || slideProcessing ? '#484f58' : '#f5e6a3',
          border: '1px solid #f5e6a3',
          borderRadius: '8px',
          fontSize: '15px',
          fontWeight: '600',
          cursor: !slideFile || slideProcessing ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => { if (slideFile && !slideProcessing) e.currentTarget.style.backgroundColor = '#f5e6a315'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {slideProcessing ? (
          <><span style={{
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: '2px solid #f5e6a340',
            borderTop: '2px solid #f5e6a3',
            borderRadius: '50%',
            animation: 'slideSpinner 0.8s linear infinite'
          }} /> Processing...</>
        ) : (
          <>Upload & Process</>
        )}
      </button>

      {/* Error */}
      {slideError && (
        <div style={{
          marginTop: '16px', padding: '12px 16px', backgroundColor: '#f8514922',
          border: '1px solid #f85149', borderRadius: '8px', color: '#f85149', fontSize: '14px'
        }}>
          ❌ {slideError}
        </div>
      )}

      {/* Download Result */}
      {slideResult && (
        <div style={{
          marginTop: '16px', padding: '16px', backgroundColor: '#3fb95015',
          border: '1px solid #3fb950', borderRadius: '8px',
          display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>✅</span>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#3fb950', margin: '0 0 4px 0', fontWeight: '600' }}>Processing complete!</p>
            <p style={{ color: '#f5e6a3', margin: 0, fontSize: '13px' }}>{slideResultFilename}</p>
          </div>
          <a
            href={slideResult}
            download={slideResultFilename}
            style={{
              padding: '8px 16px', backgroundColor: '#238636', color: '#fff',
              borderRadius: '6px', textDecoration: 'none', fontWeight: '600',
              fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2ea043'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#238636'}
          >
            ⬇ Download
          </a>
        </div>
      )}
    </div>
  );

  // ==================== PRACTICE PROCESSOR ====================
  const handlePracticeUpload = async () => {
    if (!practiceProcFile) return;
    setPracticeProcProcessing(true);
    setPracticeProcError(null);
    setPracticeProcResult(null);
    try {
      const formData = new FormData();
      formData.append('file', practiceProcFile);
      const response = await apiFetch(`${API_BASE_URL}/api/process-slide-practice`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }
      const disposition = response.headers.get('Content-Disposition');
      let filename;
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*?)\1(;|$)/);
        if (match) filename = match[2];
      }
      if (!filename) {
        const origName = practiceProcFile.name;
        const dotIndex = origName.lastIndexOf('.');
        if (dotIndex > 0) {
          filename = origName.substring(0, dotIndex) + '_Practices_PySage_Processed' + origName.substring(dotIndex);
        } else {
          filename = origName + '_Practices_PySage_Processed';
        }
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPracticeProcResult(url);
      setPracticeProcResultFilename(filename);
    } catch (err) {
      console.error('Practice processing error:', err);
      setPracticeProcError(err.message);
    } finally {
      setPracticeProcProcessing(false);
    }
  };

  const handlePracticeDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPracticeProcDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setPracticeProcFile(e.dataTransfer.files[0]);
      setPracticeProcResult(null);
      setPracticeProcError(null);
    }
  };

  const renderPracticeProcessorSection = () => (
    <div style={{ padding: '32px', overflowY: 'auto', height: '100%' }}>
      <style>{`
        @keyframes practiceSpinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <h2 style={{ color: '#aff5b4', marginBottom: '8px', fontSize: '24px' }}>📝 Generate PySage Slide Practice</h2>
      <p style={{ color: '#f5e6a3', marginBottom: '24px', fontSize: '14px' }}>
        Upload a slide file to extract practice problems via the PySage backend. The processed file will be available for download.
      </p>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setPracticeProcDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setPracticeProcDragOver(false); }}
        onDrop={handlePracticeDrop}
        onClick={() => document.getElementById('practice-file-input').click()}
        style={{
          border: practiceProcDragOver ? '2px solid #58a6ff' : '2px dashed #f5e6a3',
          borderRadius: '12px',
          padding: '48px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: practiceProcDragOver ? '#388bfd15' : '#161b22',
          transition: 'all 0.2s',
          marginBottom: '24px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#58a6ff'; e.currentTarget.style.backgroundColor = '#388bfd10'; }}
        onMouseLeave={(e) => {
          if (!practiceProcDragOver) {
            e.currentTarget.style.borderColor = '#f5e6a3';
            e.currentTarget.style.backgroundColor = '#2d333b';
          }
        }}
      >
        <input
          id="practice-file-input"
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              setPracticeProcFile(e.target.files[0]);
              setPracticeProcResult(null);
              setPracticeProcError(null);
            }
          }}
        />
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📂</span>
        <p style={{ color: '#e6edf3', fontSize: '16px', margin: '0 0 4px 0' }}>
          Drag & drop a slide file here
        </p>
        <p style={{ color: '#f5e6a3', fontSize: '13px', margin: 0 }}>
          or click to browse
        </p>
      </div>

      {/* Selected File Info */}
      {practiceProcFile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', backgroundColor: '#2d333b',
          borderRadius: '8px', marginBottom: '16px', border: '1px solid rgba(255, 215, 102, 0.25)'
        }}>
          <span style={{ fontSize: '20px' }}>📄</span>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px', fontWeight: '500' }}>{practiceProcFile.name}</p>
            <p style={{ color: '#f5e6a3', margin: 0, fontSize: '12px' }}>{(practiceProcFile.size / 1024).toFixed(1)} KB</p>
          </div>
          <span style={{ color: '#3fb950', fontSize: '16px' }}>✓ Selected</span>
          <button
            onClick={(e) => { e.stopPropagation(); setPracticeProcFile(null); setPracticeProcResult(null); setPracticeProcError(null); }}
            style={{
              background: 'none', border: 'none', color: '#f85149',
              cursor: 'pointer', fontSize: '16px', padding: '4px'
            }}
            onMouseOver={(e) => e.target.style.color = '#ff0000'}
            onMouseOut={(e) => e.target.style.color = '#f85149'}
          >✕</button>
        </div>
      )}

      {/* Upload Button */}
      <button
        onClick={handlePracticeUpload}
        disabled={!practiceProcFile || practiceProcProcessing}
        style={{
          padding: '12px 24px',
          backgroundColor: 'transparent',
          color: !practiceProcFile || practiceProcProcessing ? '#484f58' : '#f5e6a3',
          border: '1px solid #f5e6a3',
          borderRadius: '8px',
          fontSize: '15px',
          fontWeight: '600',
          cursor: !practiceProcFile || practiceProcProcessing ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => { if (practiceProcFile && !practiceProcProcessing) e.currentTarget.style.backgroundColor = '#f5e6a315'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {practiceProcProcessing ? (
          <><span style={{
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: '2px solid #f5e6a340',
            borderTop: '2px solid #f5e6a3',
            borderRadius: '50%',
            animation: 'practiceSpinner 0.8s linear infinite'
          }} /> Processing...</>
        ) : (
          <>Upload & Process</>
        )}
      </button>

      {/* Error */}
      {practiceProcError && (
        <div style={{
          marginTop: '16px', padding: '12px 16px', backgroundColor: '#f8514922',
          border: '1px solid #f85149', borderRadius: '8px', color: '#f85149', fontSize: '14px'
        }}>
          ❌ {practiceProcError}
        </div>
      )}

      {/* Download Result */}
      {practiceProcResult && (
        <div style={{
          marginTop: '16px', padding: '16px', backgroundColor: '#3fb95015',
          border: '1px solid #3fb950', borderRadius: '8px',
          display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>✅</span>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#3fb950', margin: '0 0 4px 0', fontWeight: '600' }}>Processing complete!</p>
            <p style={{ color: '#f5e6a3', margin: 0, fontSize: '13px' }}>{practiceProcResultFilename}</p>
          </div>
          <a
            href={practiceProcResult}
            download={practiceProcResultFilename}
            style={{
              padding: '8px 16px', backgroundColor: '#238636', color: '#fff',
              borderRadius: '6px', textDecoration: 'none', fontWeight: '600',
              fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2ea043'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#238636'}
          >
            ⬇ Download
          </a>
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#1c2128',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: '1.5px solid rgba(255, 215, 102, 0.4)',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: '#2d333b',
        borderBottom: '1px solid rgba(255, 215, 102, 0.25)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '28px' }}>⚙️</span>
          <div>
            <h1 style={{ margin: 0, color: '#fff', fontSize: '22px' }}>Admin Panel</h1>
          </div>
        </div>
        <button
          onClick={() => { window.scrollTo(0, 0); onClose(); }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#373e47',
            color: '#ffffff',
            border: '1px solid rgba(255, 215, 102, 0.25)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '22px',
            fontWeight: '600'
          }}
          onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.borderColor = '#f85149'; }}
          onMouseOut={(e) => { e.target.style.backgroundColor = '#373e47'; e.target.style.borderColor = 'rgba(255, 215, 102, 0.25)'; }}
        >
          ✕
        </button>
      </div>

      {/* Main Content with Sidebar */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden'
      }}>
        {/* Left Sidebar - Navigation */}
        <div style={{
          width: '220px',
          backgroundColor: '#2d333b',
          borderRight: '1px solid rgba(255, 215, 102, 0.25)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <p style={{ 
            color: '#f5e6a3', 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            marginBottom: '8px',
            fontWeight: '600'
          }}>
            Navigation
          </p>
          {ADMIN_SECTIONS.map(section => (
            <button
              key={section.id}
              onClick={() => {
                setActiveSection(section.id);
                // Reset selections when switching sections
                if (section.id !== 'curriculum') {
                  setSelectedSlide(null);
                }
                if (section.id !== 'practices') {
                  setSelectedPractice(null);
                }
              }}
              style={{
                padding: '12px 16px',
                backgroundColor: activeSection === section.id ? 'rgba(175,245,180,0.15)' : 'transparent',
                border: activeSection === section.id ? '1px solid #aff5b4' : '1px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                textAlign: 'left',
                transition: 'all 0.2s',
                color: activeSection === section.id ? '#aff5b4' : '#aff5b4'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.backgroundColor = '#373e47';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{section.icon}</span>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>{section.label}</span>
            </button>
          ))}
        </div>

        {/* Right Content Area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: '#1c2128'
        }}>
          {activeSection === 'home' && renderHomeSection()}
          {activeSection === 'content-generator' && renderContentGeneratorSection()}
          {activeSection === 'curriculum' && renderCurriculumSection()}
          {activeSection === 'practices' && renderPracticesSection()}
          {activeSection === 'slide-processor' && renderSlideProcessorSection()}
          {activeSection === 'practice-processor' && renderPracticeProcessorSection()}
          {activeSection === 'question-bank' && <QuestionBankAdmin selectedSection={selectedSection} />}
          {activeSection === 'class-quiz' && <ClassQuizAdmin selectedSection={selectedSection} />}
          {activeSection === 'students' && <StudentAdmin />}
          {/* Add more section renders here */}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
